/*
 * This File is for intercepting requests using $fetch
*/
import { ofetch } from 'ofetch'
import { Capacitor } from '@capacitor/core'
import { CONFIG } from '~/services/config'
import { spoofQueryParams } from '~/utils/spoof'

export default defineNuxtPlugin(() => {
  const { environment } = useRuntimeConfig().public
  const currentExecutingRequests = ref({})
  const requestQueue = ref({})
  globalThis.$fetch = ofetch.create({
    headers: useRequestHeaders(['cookie']),
    onRequest ({ request, options }) {
      // Only override the baseURL if the apiType is defined
      if (options.apiType) {
        options.baseURL = CONFIG.baseUrl(environment, options.apiType)
      }

      /*
       | ===========================================================================
       | Capacitor (iOS/Android) has no Nitro server bundled with the app, so any
       | relative `/api/*` call would resolve to `capacitor://localhost/api/*` and
       | return the SPA's index.html. Re-route those calls to the deployed Nuxt
       | web origin so server routes (server/api/**) actually execute.
       |
       | Skips when:
       |  - a baseURL is already set (explicit override or `apiType` above)
       |  - request is already an absolute URL (http/https)
       |  - path doesn't start with `/api/`
       | ===========================================================================
       */
      if (
        !options.baseURL
        && Capacitor.isNativePlatform()
        && typeof request === 'string'
        && request.startsWith('/api/')
      ) {
        options.baseURL = CONFIG.baseUrl(environment, 'web')
      }

      let currentUrl = request
      //  get access token from local storage for JWT
      const token = getLocal('token') ? JSON.parse(getLocal('token')) : {}
      const userHashId = getLocal('auth_user') ? JSON.parse(getLocal('auth_user')) : {}
      if (!currentUrl.includes('refresh-token') && !currentUrl.includes('reset-password') && token && token.access_token) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${token.access_token}`
        }
      }
      if (userHashId && userHashId.hash_id && !currentUrl.includes('reset-password')) {
        options.query = { ...options.query }
        /*
         | ===============================================================================
         | Restricting `hash_id` addition for urls which have `signature` as default param
         | ===============================================================================
         */
        if (!options.query?.signature) {
          options.query = {
            ...options.query,
            hash_id: userHashId.hash_id
          }
        }
      }

      /*
       | User spoofing case(s) handler - read-only on the backend (GET/HEAD), see utils/spoof.js
       */
      const spoofParams = spoofQueryParams(getLocal('spoof_email'), getLocal('spoof_email_hash'))
      if (Object.keys(spoofParams).length) {
        options.query = {
          ...options.query,
          ...spoofParams
        }
      }
      const features = getLocal('features') ? JSON.parse(getLocal('features')) : {}
      if(features && Object.keys(features).length) {
        // Iterate over the localStorage keys
        for (const key in features) {
          options.query = {
            ...options.query,
            [key]: features[key]
          }
        }
      }

      // Log request
      // console.log('[fetch request]', request, options)

      // Do duplicate request cancellation if enabled
      if (options.canCancel) {
        if (currentUrl.includes('?')) {
          currentUrl = currentUrl.substr(0, currentUrl.indexOf('?'))
        }

        if (currentExecutingRequests.value[currentUrl]) {
          const cancelController = currentExecutingRequests.value[currentUrl]
          cancelController.abort() // Cancel the previous request
        }

        // Add the cancel method to the request object
        const cancelController = new AbortController()
        options.signal = cancelController.signal

        // Store the cancel controller for the current URL
        currentExecutingRequests.value[currentUrl] = cancelController
      }
      requestQueue.value[currentUrl] = currentUrl
    },

    // eslint-disable-next-line no-unused-vars
    onRequestError ({ request, options, error }) {
      if (error.name === 'AbortError') {
        // console.log('[fetch request error] Aborted by the user')
      }
      // Log error

      // eslint-disable-next-line no-console
      console.log('[fetch request error]', request, error)
    },

    onResponse ({ request, options, response }) {
      if (response && response.status) {
        // Token refresh is now handled in ApiService with proper retry logic.
        // The interceptor no longer needs to manage refresh — it only handles
        // request setup (auth headers, hash_id, etc.) and cleanup.
      }

      if (options.canCancel) {
        let currentUrl = request
        if (currentUrl.includes('?')) {
          currentUrl = currentUrl.substr(0, currentUrl.indexOf('?'))
        }
        // Clean up the cancel controller when the request is settled
        delete currentExecutingRequests.value[currentUrl]
        delete requestQueue.value[currentUrl]
      }
    },
    onResponseError ({ request, options }) {
      if (options.canCancel) {
        let currentUrl = request
        if (currentUrl.includes('?')) {
          currentUrl = currentUrl.substr(0, currentUrl.indexOf('?'))
        }
        // Clean up the cancel controller when the request is settled
        delete currentExecutingRequests.value[currentUrl]
      }
      // // Return the custom error response
    }
  })
})
